import { defer, EMPTY, merge, of, Subject, timer } from 'rxjs'
import { catchError, filter, finalize, mergeMap, takeUntil } from 'rxjs/operators'

import * as Sentry from '@sentry/browser'

/**
 * Reports a single epic failure. Kept as `[root-epic-crash]` verbatim — the
 * Playwright sweep and the acceptance criteria match on this exact string.
 */
export const reportEpicCrash = (label, error, failureCount) => {
  // eslint-disable-next-line no-console
  console.error('[root-epic-crash]', label, error)
  if (window.SENTRY_INITIALIZED && !window.DISABLE_SENTRY) {
    Sentry.captureException(error, {
      tags: {
        source: 'epic-crash',
        epicKey: label,
      },
      extra: { failureCount },
      // Distinct fingerprint from reportEpicGiveUp so the two never group
      // together under whatever action was in flight.
      fingerprint: ['epic-crash', label],
    })
  }
}

/**
 * Reports that an epic (or leaf) exceeded RESUBSCRIBE_LIMIT failures inside
 * RESUBSCRIBE_WINDOW and has been given up on (the stream completed via
 * EMPTY). Distinct fingerprint from reportEpicCrash — see above.
 */
export const reportEpicGiveUp = (label, error, failureCount) => {
  // eslint-disable-next-line no-console
  console.error('[root-epic-give-up]', label, error)
  if (window.SENTRY_INITIALIZED && !window.DISABLE_SENTRY) {
    Sentry.captureException(error, {
      tags: {
        source: 'epic-give-up',
        epicKey: label,
      },
      extra: { failureCount },
      fingerprint: ['epic-give-up', label],
    })
  }
}

export const RESUBSCRIBE_LIMIT = 5 // failures allowed inside the window
export const RESUBSCRIBE_WINDOW = 60000 // ms
export const RESUBSCRIBE_DELAY = 1000 // ms before re-subscribing

/**
 * Wraps `factory` (a zero-arg function returning an Observable) so that:
 *  - a SYNCHRONOUS throw from `factory` becomes a caught error notification
 *    instead of killing the root epic subscription (and every future
 *    injectEpic with it). `defer` is what buys this: the factory
 *    only runs on subscribe, inside the Observable machinery, where `.catch`
 *    can see it — a bare synchronous throw from a factory called directly
 *    would propagate out of the root subscription instead.
 *  - failures are bounded: RESUBSCRIBE_LIMIT failures inside
 *    RESUBSCRIBE_WINDOW end in a fingerprinted give-up and EMPTY, rather than
 *    flapping forever.
 *  - `rekick` (optional, per-leaf) fires ONLY on resubscribe (attemptIndex >
 *    0), because a self-driving loop (one that only re-runs when it sees its
 *    own SUCCESS/ERROR action, e.g. polling) is not restarted by resubscribe
 *    alone — resubscribe re-establishes the subscription but emits no action
 *    to kick the loop back into motion. `merge` subscribes `source` first,
 *    so the epic's action$ subscription is established before the kick is
 *    emitted — the epic cannot miss it.
 */
export const isolateOne = (label, factory, rekick) => {
  let failures = []
  const attempt = (attemptIndex) => {
    const source = defer(factory)
    const kicked = attemptIndex > 0 && rekick ? merge(source, of(rekick())) : source
    return kicked.pipe(
      catchError((error) => {
        const now = Date.now()
        failures = failures.filter((t) => now - t < RESUBSCRIBE_WINDOW).concat(now)
        reportEpicCrash(label, error, failures.length)
        if (failures.length > RESUBSCRIBE_LIMIT) {
          reportEpicGiveUp(label, error, failures.length)
          return EMPTY
        }
        return timer(RESUBSCRIBE_DELAY).pipe(mergeMap(() => attempt(attemptIndex + 1)))
      })
    )
  }
  return attempt(0)
}

/**
 * A leaf is either a plain epic function, or `{ epic, rekick }`
 * (`rekick: () => action`).
 *
 * Granularity: isolate at the LEAF, re-kick at the LEAF, eject at the
 * BARREL.
 *
 * Nested sub-barrels that keep plain `combineEpics` (rather than
 * `combineEpicsIsolated`) count as ONE leaf here, and therefore share a
 * SINGLE 5-failures-per-60s budget and a single give-up between everything
 * inside them; the crash label degrades to the sub-barrel's name. That is
 * an accepted tradeoff — it still strictly improves on no isolation at all.
 * The remedy when a give-up fires on such a sub-barrel is to convert it to
 * `combineEpicsIsolated` too, which subdivides both the budget and the
 * label.
 */
export const combineEpicsIsolated = (...leaves) => {
  const combined = (action$, handles, deps) =>
    merge(
      ...leaves.map((entry) => {
        const leaf = typeof entry === 'function' ? entry : entry.epic
        const rekick = typeof entry === 'function' ? undefined : entry.rekick
        return isolateOne(
          // eslint-disable-next-line no-underscore-dangle
          `${deps.__registryKey || '?'}:${leaf.name || 'anon'}`,
          () => leaf(action$, handles, deps),
          rekick
        )
      })
    )
  // eslint-disable-next-line no-underscore-dangle
  combined.__isolated = true // asserted by epicKeys.test.js's union invariant
  return combined
}

/**
 * Builds the inject/eject registry. `runEpic` is the caller's way of handing
 * a root-level epic function to the underlying epic stream — today that's
 * `(e) => epic$.next(e)` (see state$.js), with the public surface
 * (injectEpic, ejectEpic, getRunningEpicKeys) staying stable if that
 * mechanism ever changes.
 */
export const createEpicRegistry = ({ runEpic }) => {
  const running = new Set()
  const ejected$ = new Subject() // carries only eject keys

  const lifetime = (key, epic) => (action$, state$, deps) =>
    isolateOne(
      key,
      () => epic(action$, state$, { ...deps, __registryKey: key }) // eslint-disable-line no-underscore-dangle
    ).pipe(
      takeUntil(ejected$.pipe(filter((k) => k === key))),
      finalize(() => {
        running.delete(key)
      })
    )

  return {
    injectEpic: (key, epic) => {
      if (running.has(key)) return false
      running.add(key)
      runEpic(lifetime(key, epic))
      return true
    },
    ejectEpic: (key) => {
      if (!running.has(key)) return false
      running.delete(key)
      ejected$.next(key)
      return true
    },
    getRunningEpicKeys: () => Array.from(running),
  }
}
